/**
 * @license
 * @copyright Copyright Motili Inc., 2021 All Rights Reserved
 */

import { createSlice, PayloadAction, Action } from '@reduxjs/toolkit';
import {
    CompanyInfo,
    ContractorDocuments,
    FormState,
    PartsEquipmentOrdering,
    StarterInfo,
    TeamInfo,
    Trade,
} from '../../types';

const initialState: FormState = {
    role: 'contractor',
    UUID: '',
    status: 'IN_PROGRESS',
    signatureId: '',
    starterInfo: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        smsOptIn: false,
    },
    contractor: {
        totalSteps: 6,
        trades: [],
        companyInfo: {
            name: '',
            website: '',
            corporationType: '',
            companyAddress: {
                address1: '',
                address2: '',
                city: '',
                state: '',
                zip: '',
            },
            billingAddress: {
                address1: '',
                address2: '',
                city: '',
                state: '',
                zip: '',
            },
            useCompanyAddressAsBilling: true,
        },
        teamInfo: {
            systemInstalls: '',
            serviceCalls: '',
            masterTechs: '',
            employees: '',
            singleFamilyServiceProvider: false,
            multiDwellingServiceProvider: false,
            commercialServiceProvider: false,
        },
        serviceArea: '',
        documents: {
            epaCertification: [],
            secretaryOfStateRegistration: [],
            w9: [],
            tradeLicenses: [],
            certificateOfLiability: [],
            taxSalesCertificate: [],
        },
        registerForLaborJobs: false,
        partsEquipmentOrdering: {
            forMyCompany: false,
            onBehalfOfClient: false,
            neither: false,
            clients: [],
        },
    },
};

type SubCategoryPayload = {
    type: string;
    categories: string[];
};

const registrationFormSlice = createSlice({
    name: 'registrationForm',
    initialState,
    reducers: {
        setRole(state, action: PayloadAction<string>) {
            state.role = action.payload;
        },
        setUUID(state, action: PayloadAction<string>) {
            state.UUID = action.payload;
        },
        setStatus(state, action: PayloadAction<string>) {
            state.status = action.payload;
        },
        setSignatureId(state, action: PayloadAction<string>) {
            state.signatureId = action.payload;
        },
        setStarterInfo(state, action: PayloadAction<StarterInfo>) {
            state.starterInfo = action.payload;
        },
        setTrades(state, action: PayloadAction<Trade[]>) {
            state.contractor.trades = action.payload;
        },
        setSubCategories(state, action: PayloadAction<SubCategoryPayload>) {
            const { type, categories } = action.payload;
            let updatedTrades = state.contractor.trades.map(trade => {
                return trade.type === type
                    ? { ...trade, subCategories: categories }
                    : trade;
            });
            state.contractor.trades = updatedTrades;
        },
        setCompanyInfo(state, action: PayloadAction<CompanyInfo>) {
            state.contractor.companyInfo = action.payload;
        },
        setTeamInfo(state, action: PayloadAction<TeamInfo>) {
            state.contractor.teamInfo = action.payload;
        },
        setServiceArea(state, action: PayloadAction<string>) {
            state.contractor.serviceArea = action.payload;
        },
        setDocuments(state, action: PayloadAction<ContractorDocuments>) {
            state.contractor.documents = action.payload;
        },
        setRegisterForLaborJobs(state, action: PayloadAction<boolean>) {
            state.contractor.registerForLaborJobs = action.payload;
        },
        setPartsEquipmentOrdering(
            state,
            action: PayloadAction<PartsEquipmentOrdering>
        ) {
            state.contractor.partsEquipmentOrdering = action.payload;
        },
        resetState(state, action: Action) {
            return initialState;
        },
    },
});

export const {
    setRole,
    setUUID,
    setStatus,
    setSignatureId,
    setStarterInfo,
    setTrades,
    setSubCategories,
    setCompanyInfo,
    setTeamInfo,
    setServiceArea,
    setDocuments,
    setRegisterForLaborJobs,
    setPartsEquipmentOrdering,
    resetState,
} = registrationFormSlice.actions;

export default registrationFormSlice.reducer;
